import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import type { QueryInput } from "./entity-instance.service";
import type { EntityWidgetQuery, WidgetAggregate, WidgetDimension } from "@/types/dashboards";

export interface EntityAggregateArgs {
  entityName: string;
  metrics: WidgetAggregate[];
  /** Canonical pylo `filter.query` tree, stored verbatim on the widget. */
  filter?: QueryInput[];
  dimensions?: WidgetDimension[];
  sort?: { field: string; order: "asc" | "desc" }[];
  limit?: number;
}

export interface EntityAggregateResult {
  rows: Record<string, unknown>[];
  total: Record<string, unknown>;
}

const document = gql`
  query entityAggregate($entityName: String!, $filter: EntityInstanceAggregateInput!) {
    entityInstanceAggregate(entityName: $entityName, filter: $filter) {
      rows
      total
    }
  }
`;

interface EntityAggregateResponse {
  entityInstanceAggregate: {
    rows: Record<string, unknown>[];
    total: Record<string, unknown>;
  };
}

/**
 * Serialize `WidgetAggregate[]` to the backend's `AggregateInput[]`. Shared by
 * the entity and event aggregate services so both sources send identical metric
 * shapes (count defaults its field to `"*"`).
 */
export const metricsToInput = (metrics: WidgetAggregate[]) =>
  metrics.map((m) => ({ function: m.function, field: m.field || "*", alias: m.alias }));

/**
 * The viewer's IANA timezone (e.g. "Europe/Berlin"). Used as the default
 * bucketing timezone so day/hour buckets align to the viewer's local calendar
 * — matching the dashboard date-range bounds, which also snap to local midnight
 * (see `resolveDashboardDateRange`). Without this the backend buckets in UTC,
 * which leaves the first bar of a "last N days" chart cut off by the local↔UTC
 * offset.
 */
const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Translate the frontend's discriminated-union `WidgetDimension[]` to the
 * backend's polymorphic `DimensionInput[]` (one variant field set per element).
 * Shared by both aggregate sources. The `relation` variant collapses to the
 * dotted `field` form; event widgets never produce it.
 *
 * timeBucket dimensions default their `timezone` to the viewer's local zone so
 * buckets line up with the locally-anchored date-range bounds; an explicit
 * `tb.timezone` on the widget still wins.
 */
export const dimensionsToInput = (dimensions: WidgetDimension[]) =>
  dimensions.map((dim) => {
    if (dim.kind === "field") return { field: dim.field };
    if (dim.kind === "relation") return { field: [...dim.relationPath, dim.field].join(".") };
    const tb = dim.timeBucket;
    return {
      timeBucket: {
        field: tb.field,
        interval: tb.interval,
        timezone: tb.timezone || browserTimeZone(),
      },
    };
  });

export const runEntityAggregateQuery = async (
  args: EntityAggregateArgs,
): Promise<EntityAggregateResult> => {
  const client = createGraphQLClient({ endpointName: "entityInstanceAggregate" });

  const filter: Record<string, unknown> = {
    aggregate: args.metrics.map((m) => ({
      function: m.function,
      field: m.field || "*",
      alias: m.alias,
    })),
  };
  // The widget stores the canonical pylo query tree, so pass it straight through.
  if (args.filter && args.filter.length > 0) {
    filter.query = args.filter;
  }
  if (args.dimensions && args.dimensions.length > 0) {
    filter.dimensions = dimensionsToInput(args.dimensions);
  }
  if (args.sort && args.sort.length > 0) {
    filter.sortby = args.sort.map((s) => ({ field: s.field, order: s.order }));
  }
  if (args.limit) filter.limit = args.limit;

  const response = await client.request<EntityAggregateResponse>(document, {
    entityName: args.entityName,
    filter,
  });

  return {
    rows: response.entityInstanceAggregate.rows ?? [],
    total: response.entityInstanceAggregate.total ?? {},
  };
};

export const entityWidgetQueryToArgs = (query: EntityWidgetQuery): EntityAggregateArgs => ({
  entityName: query.entityName,
  metrics: query.metrics,
  filter: query.filter,
  dimensions: query.dimensions,
  sort: query.sort,
  limit: query.limit,
});
